import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 화면 위에 말풍선을 띄워 버튼의 의미와 업무 흐름을 순서대로 설명하는 튜토리얼 오버레이.
 *
 * 스텝 한 개의 모양:
 *   {
 *     target: '[data-tour="new-product"]',  // 가리킬 요소 (없으면 화면 가운데에 그냥 띄운다)
 *     eyebrow: '상품 목록 → 새 상품 등록',   // 말풍선 맨 윗줄 (흐름 표시)
 *     title: '상품을 새로 만드는 버튼입니다',
 *     body: ['문단1', '문단2'],             // 문자열 하나만 줘도 된다
 *     placement: 'bottom',                  // 생략하면 공간을 보고 알아서 정한다
 *     spotlightPadding: 8,
 *   }
 */

const CARD_WIDTH = 400;
const GAP = 14; // 대상과 말풍선 사이 간격
const EDGE = 12; // 화면 가장자리 최소 여백
const ARROW = 10;

function useTargetRect(selector, depsKey) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    let el = null;
    try {
      el = document.querySelector(selector);
    } catch (_) {
      el = null;
    }
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return;
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [selector]);

  useLayoutEffect(() => {
    measure();
    // 대상이 늦게 그려지는 경우(데이터 로딩 등)를 대비해 잠깐 재시도한다.
    const timers = [50, 150, 350, 700].map((ms) => setTimeout(measure, ms));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure, depsKey]);

  return rect;
}

/** 대상 위치와 말풍선 크기를 보고 놓을 자리를 정한다. */
function computePlacement(rect, cardSize, preferred) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(CARD_WIDTH, vw - EDGE * 2);
  const cardH = cardSize.height || 220;

  if (!rect) {
    return {
      placement: 'center',
      style: {
        width: cardW,
        top: Math.max(EDGE, (vh - cardH) / 2),
        left: Math.max(EDGE, (vw - cardW) / 2),
      },
      arrow: null,
    };
  }

  const fitsBelow = rect.bottom + GAP + cardH <= vh - EDGE;
  const fitsAbove = rect.top - GAP - cardH >= EDGE;
  const fitsRight = rect.right + GAP + cardW <= vw - EDGE;
  const fitsLeft = rect.left - GAP - cardW >= EDGE;

  const order = [];
  if (preferred) order.push(preferred);
  order.push('bottom', 'top', 'right', 'left');

  const can = { bottom: fitsBelow, top: fitsAbove, right: fitsRight, left: fitsLeft };
  const placement = order.find((p) => can[p]) || 'bottom';

  const clampX = (x) => Math.min(Math.max(x, EDGE), Math.max(EDGE, vw - cardW - EDGE));
  const clampY = (y) => Math.min(Math.max(y, EDGE), Math.max(EDGE, vh - cardH - EDGE));

  let top;
  let left;
  if (placement === 'bottom' || placement === 'top') {
    left = clampX(rect.left + rect.width / 2 - cardW / 2);
    top = placement === 'bottom' ? rect.bottom + GAP : rect.top - GAP - cardH;
    top = clampY(top);
  } else {
    top = clampY(rect.top + rect.height / 2 - cardH / 2);
    left = placement === 'right' ? rect.right + GAP : rect.left - GAP - cardW;
    left = clampX(left);
  }

  // 화살표는 말풍선 기준 상대 좌표
  let arrow = null;
  const targetCx = rect.left + rect.width / 2;
  const targetCy = rect.top + rect.height / 2;
  if (placement === 'bottom' || placement === 'top') {
    const x = Math.min(Math.max(targetCx - left, 22), cardW - 22);
    arrow = { side: placement, x, y: placement === 'bottom' ? 0 : cardH };
  } else {
    const y = Math.min(Math.max(targetCy - top, 22), cardH - 22);
    arrow = { side: placement, x: placement === 'right' ? 0 : cardW, y };
  }

  return { placement, style: { width: cardW, top, left }, arrow };
}

const TourGuide = ({ steps = [], open = false, onClose, onFinish }) => {
  const [index, setIndex] = useState(0);
  const [cardSize, setCardSize] = useState({ width: CARD_WIDTH, height: 220 });
  const cardRef = useRef(null);

  const total = steps.length;
  const step = steps[index] || null;
  const rect = useTargetRect(open ? step?.target : null, `${open ? 1 : 0}:${index}`);

  // 스텝이 바뀌면 대상이 화면 안에 들어오도록 스크롤한다.
  useEffect(() => {
    if (!open || !step?.target) return;
    let el = null;
    try {
      el = document.querySelector(step.target);
    } catch (_) {
      el = null;
    }
    if (!el) return;
    const r = el.getBoundingClientRect();
    const offscreen = r.top < 80 || r.bottom > window.innerHeight - 80;
    if (offscreen) {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {
        el.scrollIntoView();
      }
    }
  }, [open, index, step]);

  useLayoutEffect(() => {
    if (!open || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    if (Math.abs(r.height - cardSize.height) > 2 || Math.abs(r.width - cardSize.width) > 2) {
      setCardSize({ width: r.width, height: r.height });
    }
  });

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const close = useCallback(
    (completed) => {
      if (completed && onFinish) onFinish();
      if (onClose) onClose(completed);
    },
    [onClose, onFinish]
  );

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => {
    if (index + 1 >= total) {
      close(true);
      return;
    }
    setIndex(index + 1);
  }, [index, total, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, goNext, goPrev]);

  if (!open || !step) return null;

  const pad = step.spotlightPadding ?? 8;
  const { placement, style, arrow } = computePlacement(rect, cardSize, step.placement);
  const bodyLines = Array.isArray(step.body) ? step.body : step.body ? [step.body] : [];
  const isLast = index + 1 >= total;

  const arrowStyle = (() => {
    if (!arrow) return null;
    const base = {
      position: 'absolute',
      width: ARROW * 2,
      height: ARROW * 2,
      background: '#ffffff',
      transform: 'rotate(45deg)',
      boxShadow: '0 0 0 1px rgba(15,23,42,0.06)',
    };
    if (arrow.side === 'bottom') return { ...base, top: -ARROW, left: arrow.x - ARROW };
    if (arrow.side === 'top') return { ...base, bottom: -ARROW, left: arrow.x - ARROW };
    if (arrow.side === 'right') return { ...base, left: -ARROW, top: arrow.y - ARROW };
    return { ...base, right: -ARROW, top: arrow.y - ARROW };
  })();

  return createPortal(
    <div className="fixed inset-0 z-[100000]" role="dialog" aria-modal="true" aria-label="튜토리얼">
      {/* 배경 어둡게 + 대상만 뚫어서 보여주기 */}
      {rect ? (
        <div
          className="pointer-events-none absolute transition-all duration-200"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 3px rgba(79, 70, 229, 0.9)',
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: 'rgba(15, 23, 42, 0.55)' }} />
      )}

      {/* 배경 클릭은 닫기 */}
      <div className="absolute inset-0" onClick={() => close(false)} />

      <div
        ref={cardRef}
        className="rounded-xl bg-white p-5 shadow-2xl"
        style={{ ...style, position: 'fixed' }}
        onClick={(e) => e.stopPropagation()}
      >
        {arrowStyle && <div style={arrowStyle} aria-hidden="true" />}

        <div className="relative">
          <button
            type="button"
            onClick={() => close(false)}
            aria-label="튜토리얼 닫기"
            className="absolute -right-1 -top-1 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>

          {step.eyebrow && (
            <div className="pr-7 text-[11px] font-semibold tracking-wide text-indigo-600">
              {step.eyebrow}
              <span className="ml-2 font-medium text-slate-400">
                {total}단계 중 {index + 1}
              </span>
            </div>
          )}

          <h3 className="mt-2 pr-7 text-[17px] font-bold leading-snug text-slate-900">{step.title}</h3>

          <div className="mt-3 space-y-2.5 text-[13.5px] leading-relaxed text-slate-600">
            {bodyLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className="block rounded-full transition-all"
                  style={{
                    width: i === index ? 18 : 6,
                    height: 6,
                    background: i === index ? '#4f46e5' : '#cbd5e1',
                  }}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={index === 0}
                className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                이전
              </button>
              <button
                type="button"
                onClick={goNext}
                className="rounded-lg bg-indigo-600 px-4 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                {isLast ? '완료' : '다음'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TourGuide;
